import { useEffect } from 'react';

// Components
import BrandSelectorModal from '../BrandSelectorModal/BrandSelectorModal';
import CreateNewCampaignModalV2 from 'components/CreateNewCampaignModalV2';
import CreateNewWebsiteModalV2 from 'components/shareable/CreateNewWebsiteModalV2';

// Hooks
import useGlobalModals, { GlobalModalType } from 'Hooks/useGlobalModals';
import type { CreateWebsiteConfig, CreateCampaignConfig } from 'Hooks/useGlobalModals';
import useBrand from 'Hooks/useBrand';
import useCampaignBuilder from 'Hooks/useCampaignBuilder';
import useWebsiteBuilder from 'Hooks/useWebsiteBuilder';
import { useContext } from 'react';
import { UpgradeModalContext } from 'Hooks/UpgradeModalContext';

// Utils
import { track } from 'analytics';
import useAppView from 'Hooks/useAppView';
import { useHistory } from 'react-router-dom';

/**
 * Global Modal Renderer Component
 * 
 * This component handles rendering all global modals and integrates with existing
 * modal systems to provide a seamless transition while maintaining backward compatibility.
 * 
 * It renders in MainApp and manages:
 * - Brand selection modal
 * - Campaign creation modal 
 * - Website creation modal
 * - Upgrade modal (via existing context)
 * - Future modal types
 */
const GlobalModalRenderer = () => {
    const {
        activeModals,
        isModalOpen,
        getModalConfig,
        closeModal,
        selectedBrandForCreation
    } = useGlobalModals();

    const { setCurrentBrand } = useBrand();
    const { isTeamView } = useAppView();
    const { location } = useHistory();
    const { handleOpenModal: openUpgradeModal } = useContext(UpgradeModalContext);

    // Integration with existing campaign builder
    const {
        showCreateNewCampaignModal,
        setShowCreateNewCampaignModal,
        setNewCampaignAdPlatformFilter
    } = useCampaignBuilder();

    // Integration with existing website builder  
    const {
        showCreateNewWebsiteModalV2,
        setShowCreateNewWebsiteModalV2,
        setSelectedTemplateCategory,
        setInitialWebsiteType
    } = useWebsiteBuilder();

    // Handle campaign modal integration
    useEffect(() => {
        const isCampaignModalOpen = isModalOpen(GlobalModalType.CREATE_CAMPAIGN);
        const campaignConfig = getModalConfig<CreateCampaignConfig>(GlobalModalType.CREATE_CAMPAIGN);

        if (isCampaignModalOpen && !showCreateNewCampaignModal) {
            // Note: We deliberately DON'T call setCurrentBrand() here to avoid polluting app state
            // The creation flow uses selectedBrandForCreation, which is separate from app brand state

            // Set ad platform if specified
            if (campaignConfig?.adPlatform) {
                setNewCampaignAdPlatformFilter(campaignConfig.adPlatform);
            }

            // Track modal opening
            if (campaignConfig?.source) {
                track('Campaign Creation Modal Opened', {
                    source: campaignConfig.source,
                    adPlatform: campaignConfig?.adPlatform || 'not_specified',
                    hasBrandPreselected: Boolean(campaignConfig?.selectedBrand)
                });
            }

            // Open the existing modal
            setShowCreateNewCampaignModal(true);
        }

        // Handle closing from global state
        if (!isCampaignModalOpen && showCreateNewCampaignModal) {
            setShowCreateNewCampaignModal(false);
        }
    }, [
        isModalOpen(GlobalModalType.CREATE_CAMPAIGN),
        showCreateNewCampaignModal,
        selectedBrandForCreation
    ]);

    // Handle website modal integration
    useEffect(() => {
        const isWebsiteModalOpen = isModalOpen(GlobalModalType.CREATE_WEBSITE);
        const websiteConfig = getModalConfig<CreateWebsiteConfig>(GlobalModalType.CREATE_WEBSITE);

        if (isWebsiteModalOpen && !showCreateNewWebsiteModalV2) {
            // Note: We deliberately DON'T call setCurrentBrand() here to avoid polluting app state
            // The creation flow uses selectedBrandForCreation, which is separate from app brand state

            // Set template category if specified
            if (websiteConfig?.templateCategory) {
                setSelectedTemplateCategory(websiteConfig.templateCategory);
            }

            // Set website type if specified
            if (websiteConfig?.websiteType) {
                setInitialWebsiteType(websiteConfig.websiteType);
            }

            // Track modal opening
            if (websiteConfig?.source) {
                track('Website Creation Modal Opened', {
                    source: websiteConfig.source,
                    templateCategory: websiteConfig?.templateCategory || 'not_specified',
                    websiteType: websiteConfig?.websiteType || 'not_specified',
                    hasBrandPreselected: Boolean(websiteConfig?.selectedBrand)
                });
            }

            // Open the existing modal
            setShowCreateNewWebsiteModalV2(true);
        }

        // Handle closing from global state
        if (!isWebsiteModalOpen && showCreateNewWebsiteModalV2) {
            setShowCreateNewWebsiteModalV2(false);
        }
    }, [
        isModalOpen(GlobalModalType.CREATE_WEBSITE),
        showCreateNewWebsiteModalV2,
        selectedBrandForCreation
    ]);


    // Handle modal cleanup when existing modals close
    useEffect(() => {
        if (!showCreateNewCampaignModal && isModalOpen(GlobalModalType.CREATE_CAMPAIGN)) {
            closeModal(GlobalModalType.CREATE_CAMPAIGN);
        }
    }, [showCreateNewCampaignModal]);

    useEffect(() => {
        if (!showCreateNewWebsiteModalV2 && isModalOpen(GlobalModalType.CREATE_WEBSITE)) {
            closeModal(GlobalModalType.CREATE_WEBSITE);
        }
    }, [showCreateNewWebsiteModalV2]);

    const onCloseWebsiteModal = () => {
        // If we're in team view and not on the website builder page, reset the current brand
        // when the website modal is closed
        if (isTeamView() && !location.pathname.includes('/website-builder')) {
            setCurrentBrand(undefined);
        }
    }

    return (
        <>
            {/* Brand Selector Modal - Always render through global system */}
            <BrandSelectorModal />

            {/* Campaign Creation Modal - Centralized rendering (always as modal) */}
            {(showCreateNewCampaignModal || isModalOpen(GlobalModalType.CREATE_CAMPAIGN)) && (
                <CreateNewCampaignModalV2
                    className="globalCreateNewCampaignModal"
                    isModal={true}
                />
            )}

            {/* Website Creation Modal - Centralized rendering (always as modal) */}
            {(showCreateNewWebsiteModalV2 || isModalOpen(GlobalModalType.CREATE_WEBSITE)) && (
                <CreateNewWebsiteModalV2
                    onModalClose={onCloseWebsiteModal}
                    className="globalCreateNewWebsiteModal"
                    isModal={true}
                    isRedirect={getModalConfig<CreateWebsiteConfig>(GlobalModalType.CREATE_WEBSITE)?.redirectToWebsiteBuilderImmediately}
                />
            )}


        </>
    );
};

export default GlobalModalRenderer;